import { useEffect, useState } from "react";
import {
    Button,
    Subtitle1,
    Text
} from "@fluentui/react-components";

import { Pages, useOfficeAppContext } from "../OfficeAppContext";
import { Loading } from "../components/Loading";
import { BasicLayout } from "../components/BasicLayout";

// order confirmation template
const getOrderConfirmationTemplate = () => {
    return `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <h1 style="color: #333; border-bottom: 2px solid #0078d4; padding-bottom: 10px;">Order Confirmation</h1>

        <p>Hi Customer,</p>

        <p>Thank you for your order! We've received it and are getting it ready to ship.</p>

        <h2 style="color: #333;">Order Details</h2>

        <p><strong>Order Number:</strong> #12345</p>
        <p><strong>Order Date:</strong> ${new Date().toLocaleDateString()}</p>

        <table style="border-collapse: collapse; width: 100%; margin: 20px 0;">
            <thead>
                <tr style="background-color: #f2f2f2;">
                    <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Item Name</th>
                    <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Quantity</th>
                    <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Subtotal</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td style="border: 1px solid #ddd; padding: 12px;">Sample Product A</td>
                    <td style="border: 1px solid #ddd; padding: 12px;">1</td>
                    <td style="border: 1px solid #ddd; padding: 12px;">$25.00</td>
                </tr>
                <tr>
                    <td style="border: 1px solid #ddd; padding: 12px;">Sample Product B</td>
                    <td style="border: 1px solid #ddd; padding: 12px;">2</td>
                    <td style="border: 1px solid #ddd; padding: 12px;">$40.00</td>
                </tr>
            </tbody>
            <tfoot>
                <tr style="font-weight: bold; background-color: #f9f9f9;">
                    <td colspan="2" style="border: 1px solid #ddd; padding: 12px; text-align: right;">Total:</td>
                    <td style="border: 1px solid #ddd; padding: 12px;">$65.00</td>
                </tr>
            </tfoot>
        </table>

        <p>We will send tracking information once your order ships.</p>

        <div style="margin-top: 30px; padding: 15px; background-color: #f8f9fa; border-radius: 5px;">
            <p><strong>Thanks for your business!</strong></p>
            <p>The Team at Nordoon</p>
        </div>
    </div>
  `;
};

export const EmailDetails = () => {
    const {
        selected_email,
        is_loading: context_loading,
        setSelectedEmail,
        setCurrentPage,
    } = useOfficeAppContext();

    const [email_body, setEmailBody] = useState<string>("");
    const [loading_email, setLoadingEmail] = useState<boolean>(false);

    const handleReply = () => {
        if (!selected_email) return;

        try {
            const Office = (globalThis as any).Office;
            Office.context.mailbox.displayNewMessageFormAsync({
                toRecipients: [selected_email.from?.emailAddress],
                subject: `Re: ${selected_email.subject}`,
                htmlBody: getOrderConfirmationTemplate(),
            });
        } catch (error) {
            console.error("Error creating reply:", error);
        }
    };

    const handleProcessEmail = async () => {
        if (!selected_email) return;

        setLoadingEmail(true);
        try {
            const Office = (globalThis as any).Office;

            // get email body as text for display
            const body = await new Promise<string>((resolve, reject) => {
                selected_email.body.getAsync(Office.CoercionType.Text, (result: any) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve(result.value);
                    } else {
                        reject(new Error(result.error?.message || "Failed to get email body"));
                    }
                });
            });

            setEmailBody(body);
        } catch (error: any) {
            console.error("Error processing email:", error);
        } finally {
            setLoadingEmail(false);
        }
    };

    // check if email is already processed
    useEffect(() => {
        if (!selected_email) {
            setCurrentPage(Pages.Dashboard);
            return;
        }
    }, [selected_email, setCurrentPage]);

    if (!selected_email) {
        return null;
    }

    if (context_loading) {
        return <Loading text="Loading email details..." />;
    }

    return (
        <BasicLayout
            title="Email Details"
            onBack={() => {
                setSelectedEmail(undefined);
                setCurrentPage(Pages.Dashboard);
            }}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <Subtitle1>
                    Subject: <span style={{ fontWeight: "normal" }}>{selected_email.subject}</span>
                </Subtitle1>

                <Subtitle1>
                    From: <span style={{ fontWeight: "normal" }}>{selected_email.from?.emailAddress}</span>
                </Subtitle1>

                <Subtitle1>
                    To: <span style={{ fontWeight: "normal" }}>
                        {selected_email.to?.map((email: any) => email.emailAddress).join(", ")}
                    </span>
                </Subtitle1>

                {selected_email.attachments && selected_email.attachments.length > 0 && (
                    <Subtitle1>
                        Attachments: <span style={{ fontWeight: "normal" }}>
                            {selected_email.attachments.length} file(s)
                        </span>
                    </Subtitle1>
                )}

                <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                    <Button
                        onClick={handleProcessEmail}
                        appearance="secondary"
                        disabled={loading_email}
                    >
                        {loading_email ? "Loading..." : "View Email Content"}
                    </Button>

                    <Button
                        appearance="primary"
                        onClick={handleReply}
                    >
                        Send Order Confirmation Reply
                    </Button>
                </div>

                {email_body && (
                    <div style={{
                        marginTop: "1rem",
                        padding: "1rem",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "4px",
                        maxHeight: "200px",
                        overflow: "auto"
                    }}>
                        <Text weight="bold">Email Content:</Text>
                        <Text style={{ whiteSpace: "pre-wrap", fontSize: "0.9em" }}>
                            {email_body.length > 500 ? email_body.substring(0, 500) + "..." : email_body}
                        </Text>
                    </div>
                )}
            </div>
        </BasicLayout>
    );
};